import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { Eyebrow, SectionNum } from "@/components/ui-bits";
import api from "@/lib/api";
import { ArrowUpRight, Search } from "lucide-react";

const CATEGORY_KICKER = {
  "0": "Nuclear, safeguards, and cross-referenced NRC items.",
  "1": "Chemicals, toxins, protective and detection equipment, propellants, energetic materials.",
  "2": "Machine tools, bearings, robots, isostatic presses, welding, and process technology.",
  "3": "Semiconductors, ICs, RF, quantum electronics, and general electronic assemblies.",
  "4": "Computers, quantum computers, HPC, and clustered systems.",
  "5": "Telecommunications, cryptographic and information security items.",
  "6": "Optics, lasers, cameras, sensors, sonar, radar, and acoustics.",
  "7": "Inertial nav, IMUs, avionics, GPS/GNSS receivers, and flight controls.",
  "8": "Marine vessels, submersibles, propulsion, and hydro-acoustic systems.",
  "9": "Aircraft, spacecraft, launch vehicles, engines, and related aerospace items.",
};

export default function CCL() {
  const [meta, setMeta] = useState(null);
  const [q, setQ] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/ccl/meta").then((r) => setMeta(r.data)).catch(() => setMeta({ status: "error" }));
  }, []);

  useEffect(() => {
    if (q.trim().length < 2) {
      setSearchResults(null);
      return;
    }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await api.get("/ccl/entries", { params: { q: q.trim() } });
        setSearchResults(r.data.entries.slice(0, 25));
      } catch { setSearchResults([]); }
      setSearching(false);
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const categories = useMemo(() => {
    if (!meta || !meta.categories) return [];
    return Object.entries(meta.categories).map(([num, title]) => ({
      number: num,
      title,
      count: meta.category_counts?.[num] || 0,
    }));
  }, [meta]);

  const lastSyncedStr = meta?.last_synced
    ? new Date(meta.last_synced).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })
    : "—";

  return (
    <Layout>
      <SEO
        title="Commerce Control List — Navigator"
        description="An easier way to navigate the Commerce Control List (Supplement No. 1 to 15 CFR Part 774). Browse by Category and Product Group, search by ECCN or keyword, and jump to the source on eCFR. Updated nightly from the eCFR API."
        path="/ccl"
      />

      <section className="border-b border-hairline" data-testid="ccl-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-20 pb-14">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-8">
              <Eyebrow>Reference · 15 CFR Part 774 Supp. 1</Eyebrow>
              <h1 className="display-1 text-ink mt-6 max-w-4xl">
                Commerce Control List.
              </h1>
              <p className="mt-6 text-ink-soft italic font-serif text-lg leading-snug max-w-2xl">
                A navigator for the Commerce Control List — organized by Category and Product Group, searchable by ECCN or keyword.
              </p>
              <p className="mt-6 text-ink-soft text-[1.0625rem] leading-relaxed max-w-2xl">
                The CCL identifies items subject to the Export Administration Regulations (EAR) and the reasons the U.S. Government controls them. Every ECCN below deep-links to the current text on the eCFR. This page is a navigational aid — verify against the eCFR before you rely on it.
              </p>
            </div>
            <div className="lg:col-span-4 lg:text-right space-y-1">
              <p className="section-num" data-testid="ccl-entry-count">{meta?.entry_count || "—"}</p>
              <p className="text-[11px] uppercase tracking-[0.22em] text-ink-soft">ECCNs indexed</p>
              <p className="text-[11px] uppercase tracking-[0.22em] text-ink-soft mt-3" data-testid="ccl-last-synced">
                Last synced {lastSyncedStr}
              </p>
              {meta?.source_as_of && (
                <p className="text-[10px] uppercase tracking-[0.22em] text-ink-soft">
                  eCFR as of {meta.source_as_of}
                </p>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Global search */}
      <section className="border-b border-hairline bg-ivory-alt" data-testid="ccl-search">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
          <div className="relative max-w-2xl">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-ink-soft" strokeWidth={2} />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by ECCN (e.g. 3A001) or keyword (e.g. quantum, laser)"
              className="w-full pl-11 pr-4 py-3 bg-ivory border border-hairline text-[15px] text-ink placeholder:text-ink-soft/70 focus:outline-none focus:border-brass"
              data-testid="ccl-search-input"
            />
          </div>
          {searchResults && (
            <div className="mt-6 max-w-3xl" data-testid="ccl-search-results">
              {searching && <p className="text-[13px] text-ink-soft">Searching…</p>}
              {!searching && searchResults.length === 0 && (
                <p className="text-[13px] text-ink-soft">No ECCNs match &ldquo;{q}&rdquo;.</p>
              )}
              {!searching && searchResults.length > 0 && (
                <ul className="border-t border-hairline">
                  {searchResults.map((e) => (
                    <li key={e.eccn} className="border-b border-hairline-soft">
                      <button
                        onClick={() => navigate(`/ccl/${e.category}#${e.eccn}`)}
                        className="w-full text-left flex items-start gap-4 py-3 hover:bg-ivory transition-colors -mx-3 px-3"
                        data-testid={`ccl-search-hit-${e.eccn}`}
                      >
                        <span className="font-mono text-[13px] font-semibold text-brass-deep shrink-0 w-16">{e.eccn}</span>
                        <span className="text-[14px] text-ink leading-snug">{e.heading}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Category cards */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-16" data-testid="ccl-categories">
        <Eyebrow>Categories</Eyebrow>
        <h2 className="font-serif text-3xl text-ink mt-4 mb-10">Browse by category.</h2>
        <ul className="border-t border-hairline">
          {categories.map((c) => (
            <li key={c.number} className="border-b border-hairline">
              <Link
                to={`/ccl/${c.number}`}
                className="group grid grid-cols-12 gap-6 py-8 items-baseline hover:bg-ivory-alt transition-colors -mx-3 px-3"
                data-testid={`ccl-category-card-${c.number}`}
              >
                <SectionNum className="col-span-2 sm:col-span-1">{c.number}</SectionNum>
                <div className="col-span-8 sm:col-span-8">
                  <h3 className="font-serif text-xl sm:text-2xl text-ink group-hover:text-brass transition-colors leading-tight">
                    {c.title}
                  </h3>
                  <p className="mt-1 text-ink-soft italic font-serif text-[15px] leading-snug">
                    {CATEGORY_KICKER[c.number]}
                  </p>
                </div>
                <div className="col-span-2 sm:col-span-3 text-right">
                  <span className="text-[11px] uppercase tracking-[0.22em] text-ink-soft">{c.count} ECCNs</span>
                  <ArrowUpRight className="w-4 h-4 inline-block ml-2 text-brass opacity-0 group-hover:opacity-100 transition-opacity" strokeWidth={1.5} />
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </section>

      {/* Product groups reference */}
      <section className="border-t border-hairline bg-ivory-alt" data-testid="ccl-groups-reference">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-14">
          <Eyebrow>Product Groups</Eyebrow>
          <h2 className="font-serif text-3xl text-ink mt-4 mb-8">Every ECCN belongs to one product group.</h2>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            {meta?.groups && Object.entries(meta.groups).map(([letter, title]) => (
              <div key={letter} className="border border-hairline bg-ivory p-5">
                <p className="section-num">{letter}</p>
                <p className="mt-3 text-[15px] text-ink leading-snug">{title}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="bg-ink text-ivory" data-testid="ccl-disclaimer">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-14 text-center">
          <p className="text-[11px] uppercase tracking-[0.28em] text-brass-light font-semibold mb-4">Note</p>
          <p className="font-serif text-xl sm:text-2xl leading-snug tracking-tight">
            This navigator mirrors the CCL from the public eCFR API and refreshes nightly. It is not the authoritative text and is not legal advice. Verify each entry against the eCFR before relying on it.
          </p>
        </div>
      </section>
    </Layout>
  );
}
